'use client';

import Link from 'next/link';
import { Zap, Mail, MessageCircle, MapPin, Facebook, Linkedin, Youtube } from 'lucide-react';
import { useI18n } from './i18n-provider';

export function Footer() {
  const { t, isRtl } = useI18n();

  const footerLinks = {
    services: [
      { label: 'EV Technical Training', href: '/services#training' },
      { label: 'Spare Parts Supply', href: '/services#parts' },
      { label: 'Garage Transformation', href: '/services#transformation' },
      { label: 'Battery Solution', href: '/battery-solution' },
    ],
    company: [
      { label: t.nav.about, href: '/about' },
      { label: t.nav.successStories, href: '/success-stories' },
      { label: t.nav.blog + ' & Insights', href: '/blog' },
      { label: t.nav.resources, href: '/resources' },
      { label: 'Wholesale Cooperation', href: '/wholesale' },
      { label: 'Partner Program', href: '/partners' },
      { label: 'Free Battery Diagnostic', href: '/battery-diagnostic' },
      { label: 'FAQ', href: '/faq' },
    ],
    contact: [
      { label: 'wfdnewenergy@163.com', href: 'mailto:wfdnewenergy@163.com', icon: Mail },
      { label: 'WhatsApp: +86 131 5656 6675', href: 'https://wa.me/8613156566675', icon: MessageCircle },
      { label: 'Hefei, China', href: '#', icon: MapPin },
    ],
  };
  return (
    <footer className="relative overflow-hidden bg-[oklch(0.12_0.015_220)] text-foreground">
      {/* Decorative glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-px bg-gradient-to-r from-transparent via-[oklch(0.62_0.19_150)]/40 to-transparent" />
      <div className="absolute -top-20 left-1/4 w-96 h-96 bg-[oklch(0.62_0.19_150)]/5 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link href="/" className="flex items-center gap-3 mb-6 group">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[oklch(0.62_0.19_150)] to-[oklch(0.85_0.12_140)] flex items-center justify-center shadow-lg shadow-[oklch(0.62_0.19_150)]/30">
                <Zap className="w-5 h-5 text-[oklch(0.12_0.02_150)]" strokeWidth={2.5} />
              </div>
              <div className="flex flex-col leading-tight">
                <span className="text-lg font-bold text-foreground">WFD</span>
                <span className="text-[10px] text-muted-foreground tracking-[0.15em] uppercase font-medium">
                  NEW ENERGY
                </span>
              </div>
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed mb-6">
              Empowering thousands of garages to transform to new energy.<br/>
              China-based full-stack NEV after-sales enablement service provider.
            </p>
            <div className="flex items-center gap-3">
              {[Facebook, Linkedin, Youtube].map((Icon, idx) => (
                <a
                  key={idx}
                  href="#"
                  className="w-9 h-9 rounded-full bg-[oklch(0.20_0.015_220)] border border-[oklch(0.28_0.025_170)] flex items-center justify-center text-muted-foreground hover:text-[oklch(0.72_0.22_150)] hover:border-[oklch(0.62_0.19_150)]/50 hover:shadow-[0_0_15px_oklch(0.62_0.19_150)_/_0.2] transition-all"
                  aria-label="social"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider mb-5 text-foreground/90">
              Services
            </h3>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-muted-foreground text-sm hover:text-[oklch(0.72_0.22_150)] transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider mb-5 text-foreground/90">
              About WFD
            </h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <Link
                    href={link.href}
                    className="text-muted-foreground text-sm hover:text-[oklch(0.72_0.22_150)] transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider mb-5 text-foreground/90">
              Contact Us
            </h3>
            <ul className="space-y-4">
              {footerLinks.contact.map((link) => {
                const Icon = link.icon;
                return (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      className="flex items-center gap-3 text-muted-foreground text-sm hover:text-[oklch(0.72_0.22_150)] transition-colors"
                    >
                      {Icon && <Icon className="w-4 h-4 flex-shrink-0 text-[oklch(0.62_0.19_150)]/70" />}
                      <span>{link.label}</span>
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-[oklch(0.28_0.025_170)]/50 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-muted-foreground text-sm">
            © {new Date().getFullYear()} WFD New Energy. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link href="#" className="text-muted-foreground text-sm hover:text-[oklch(0.72_0.22_150)] transition-colors">
              Privacy Policy
            </Link>
            <Link href="#" className="text-muted-foreground text-sm hover:text-[oklch(0.72_0.22_150)] transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
